/**
 * BusinessInquiriesPill — "Business inquiries" pill that opens a
 * pre-filled mailto: to the athlete's inbox OR jumps to the agent's
 * Instagram DM. Used at the top of both the /network/{slug} profile
 * card AND the /l/{slug} FLYT ID™ page — brands / recruiters / media
 * need a one-tap contact route.
 *
 * Priority order (iter641):
 *   1. `athlete.business_email` — the athlete's OWN inbox. Wins.
 *   2. Linked agent's `business_email` — from `/network/athletes/{slug}/agent`.
 *   3. `athlete.business_instagram` — falls back to the agent's DMs
 *      when nobody wants to publish an email. Opens instagram.com.
 *
 * Renders NOTHING when every field is empty.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Briefcase } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const normalizeInstagramTarget = (raw) => {
  const v = (raw || '').trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;
  const handle = v.replace(/^@/, '').split('/').pop().split('?')[0];
  if (!handle) return null;
  return `https://instagram.com/${handle}`;
};

export default function BusinessInquiriesPill({ athlete }) {
  const slug = athlete?.slug;
  const name = athlete?.name;
  const selfEmail = (athlete?.business_email || '').trim() || null;
  const selfIg = normalizeInstagramTarget(athlete?.business_instagram);
  const [agentEmail, setAgentEmail] = useState(null);
  const [agency, setAgency] = useState(null);

  useEffect(() => {
    if (!slug || selfEmail) return; // athlete-owned inbox wins
    let cancelled = false;
    axios.get(`${API}/network/athletes/${slug}/agent`)
      .then((r) => {
        if (cancelled) return;
        const a = r.data?.agent;
        if (a?.business_email) {
          setAgentEmail(a.business_email);
          setAgency(a.agency_name || a.name || 'Representation');
        }
      })
      .catch(() => { /* 404 = no agent, silent */ });
    return () => { cancelled = true; };
  }, [slug, selfEmail]);

  const email = selfEmail || agentEmail;
  const href = email
    ? `mailto:${email}?subject=${encodeURIComponent(`Business inquiry — ${name || slug}`)}&body=${encodeURIComponent(`Hi ${selfEmail ? (name || 'there') : (agency || 'team')},\n\nI'd like to discuss a business opportunity with ${name || slug}.\n\n— `)}`
    : selfIg;
  if (!href) return null;

  // Fire-and-forget click beacon so the athlete can see "brands who
  // reached out this month" on their dashboard. Never blocks the
  // link navigation — a network error just means the counter doesn't tick.
  const onClick = () => {
    try {
      import('../../lib/nativeBridge').then((m) => m.haptic('medium')).catch(() => {});
    } catch { /* noop */ }
    if (!slug) return;
    try {
      axios.post(
        `${API}/network/athletes/${slug}/business-inquiry-click`,
        null,
        { withCredentials: true, timeout: 4000 },
      ).catch(() => {});
    } catch { /* noop */ }
  };

  return (
    <a
      href={href}
      target={email ? undefined : '_blank'}
      rel={email ? undefined : 'noopener noreferrer'}
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[13px] font-semibold text-white active:scale-[0.97] transition shrink-0 bg-white/[0.06] hover:bg-white/[0.12] backdrop-blur"
      style={{
        boxShadow: '0 0 0 1px rgba(255,255,255,0.14), inset 0 1px 0 rgba(255,255,255,0.08)',
        letterSpacing: '-0.01em',
      }}
      data-testid="athlete-business-inquiries-btn"
    >
      <Briefcase className="w-3.5 h-3.5" strokeWidth={2.25} />
      Business inquiries
    </a>
  );
}
